import { useMemo } from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useProgramYear } from "@/contexts/ProgramYearContext";
import { usePreferences } from "@/contexts/PreferencesContext";
import {
  usePaymentAdjustmentConfig,
  usePracticeEligibility,
  usePracticeEligibilityNpi,
} from "@/hooks/queries/useMipsQueries";

interface UnifiedScoreCardProps {
  compositeScore: number;
  patientComplexityBonus?: number;
  onViewReport: () => void;
  variant?: 6; // Keep for future extensibility
  className?: string;
  evaluationType?: 'GROUP' | 'NPI';
  selectedNpi?: string;
}

interface PaymentConfig {
  low_scenario_input: number;
  scaling_factor: number;
}

// Payment-adjustment math lives in the shared lib (R10 extract 2026-08-03)
import { computeAdjustmentPct as calculateAdjustmentPercent } from '@/lib/payment-adjustment';

function formatCurrency(amount: number): string {
  const absAmount = Math.abs(amount);
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(absAmount);
  if (amount < 0) return '-' + formatted;
  if (amount > 0) return '+' + formatted;
  return formatted;
}

// [Data-freshness 2026-08-12] The former module-level paymentDataCache (60s
// TTL) is gone: config + eligibility now come from the shared React Query
// cache, so eligibility uploads invalidate this card like every other
// consumer (invalidatePracticeScoring → ['eligibility', practiceId] prefix).

export function UnifiedScoreCard({ 
  compositeScore, 
  onViewReport,
  className,
  evaluationType = 'GROUP',
  selectedNpi
}: UnifiedScoreCardProps) {
  const { selectedPractice } = useAuth();
  const { programYear } = useProgramYear();
  const { displayPreferences } = usePreferences();

  const practiceId = selectedPractice?.id;
  const score = Math.min(100, Math.max(0, compositeScore));
  const isNpiMode = evaluationType === 'NPI';

  const configQ = usePaymentAdjustmentConfig(programYear);
  const groupEligQ = usePracticeEligibility(isNpiMode ? null : practiceId, programYear);
  const npiEligQ = usePracticeEligibilityNpi(
    isNpiMode ? practiceId : null, programYear, isNpiMode ? selectedNpi : null);

  const config: PaymentConfig | null = configQ.data ?? null;

  // Same derivation the old fetch performed, applied to the query payloads.
  const { medicareRevenue, mipsEligibleIndividual, eligibilityMessage } = useMemo(() => {
    let revenue: number | null = null;
    let eligible: boolean | null = null;
    let message: string | null = null;

    if (isNpiMode) {
      if (!selectedNpi) return { medicareRevenue: null, mipsEligibleIndividual: null, eligibilityMessage: null };
      const data = npiEligQ.data;
      if (data == null) {
        // 404 / fetch failure — eligibility not imported for this NPI
        message = 'Eligibility data not imported. Import via configuration page';
      } else if (data.imported && data.clinician) {
        if (data.clinician.mips_eligible_individual === false) {
          eligible = false;
          message = 'Not MIPS eligible for Individual Reporting';
        } else if (data.clinician.allowed_charges != null) {
          eligible = true;
          revenue = data.clinician.allowed_charges;
        }
      }
    } else {
      const data = groupEligQ.data;
      if (data?.imported && data.practice?.allowed_charges != null) {
        revenue = data.practice.allowed_charges;
      }
    }
    return { medicareRevenue: revenue, mipsEligibleIndividual: eligible, eligibilityMessage: message };
  }, [isNpiMode, selectedNpi, npiEligQ.data, groupEligQ.data]);

  // Loaded = the queries this mode depends on have settled (placeholderData
  // keeps last-good values during background refetches, so no flash).
  const eligPending = isNpiMode
    ? (!!selectedNpi && !!practiceId && npiEligQ.isPending)
    : (!!practiceId && groupEligQ.isPending);
  const dataLoaded = !configQ.isPending && !eligPending;

  const adjustmentPercent = config ? calculateAdjustmentPercent(Math.round(score), config.low_scenario_input, config.scaling_factor) : 0;
  const dollarAmount = medicareRevenue != null ? medicareRevenue * adjustmentPercent : null;
  const hasPaymentData = config && medicareRevenue != null;
  
  // Determine if we should show the adjustment or a message
  // Check user preference first, then data availability
  const showAdjustment = displayPreferences.showPaymentAdjustment && 
    dataLoaded && hasPaymentData && dollarAmount !== null && 
    (evaluationType !== 'NPI' || mipsEligibleIndividual !== false);

  // Check if payment adjustment display is enabled by user preference
  const showPaymentSection = displayPreferences.showPaymentAdjustment;

  return (
    <div 
      onClick={onViewReport}
      className={cn(
        "rounded-lg border bg-card p-4 cursor-pointer transition-colors hover:bg-muted/50",
        className
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-6">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Composite Score</p>
            <p className="text-[30px] leading-[45px] font-semibold mt-0.5">{score.toFixed(1)}</p>
          </div>
          {showPaymentSection && (
            <>
              <div className="h-10 w-px bg-border" />
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Projected Adjustment</p>
                {showAdjustment ? (
                  <p className={cn(
                    "text-[24px] leading-[45px] font-semibold mt-0.5",
                    dollarAmount && dollarAmount > 0 && "text-green-600 dark:text-green-500",
                    dollarAmount && dollarAmount < 0 && "text-red-600 dark:text-red-500"
                  )}>
                    {formatCurrency(dollarAmount!)}
                  </p>
                ) : (
                  <div>
                    <p className="text-[24px] leading-[45px] font-semibold mt-0.5 text-muted-foreground">—</p>
                    {dataLoaded && evaluationType === 'NPI' && eligibilityMessage && (
                      <p className="text-xs text-muted-foreground mt-0.5">{eligibilityMessage}</p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Details</span>
          <ChevronRight className="h-4 w-4" />
        </div>
      </div>
    </div>
  );
}
